// Tenant List bulk feature update (System > Tenants > selection > "Update Features…").
// To add a feature to the Tenant List bulk feature update, add one entry here.
// `field` is the suffix of the tenant fields featureAccess<field> / featureEnabled<field>.
// Also select both columns in rdsApi queries/Tenant.js (buildQuery), or the dialog shows the feature as off for every tenant.
// `requires` names a feature this one needs (same rule as TenantDetail.vue and FEATURE_REQUIRES in executeMutation tenant/handler.js):
// turning Access on also turns the required feature's Access and Enabled on.
// Plain JS on purpose (no .vue imports) so the helpers can be unit-tested under vitest's node environment.
export const BULK_TOGGLE_FEATURES = [
  { key: 'associate_app', label: 'Associate App', field: 'AssociateApp' },
  { key: 'cvpl', label: 'Checklists & Vehicle Photo Logs', field: 'ChecklistsAndVehiclePhotoLogs' },
  { key: 'custom_email_sending', label: 'Custom Email Sending', field: 'CustomEmailSending' },
  { key: 'filesm', label: 'File Management', field: 'FilesManagement' },
  { key: 'hera_messenger_2', label: 'Hera Messenger 2', field: 'HeraMessenger2' },
  { key: 'hierarchical_permissions', label: 'Hierarchical Permissions', field: 'HierarchicalPermissions' },
  { key: 'inventorym', label: 'Inventory Management', field: 'InventoryManagement' },
  { key: 'phone_automation', label: 'Phone Automation', field: 'PhoneAutomation' },
  { key: 'rchecklist', label: 'Roster Checklist', field: 'RosterChecklist' },
  { key: 'scheduling_and_rostering', label: 'Scheduling & Rostering', field: 'SchedulingAndRostering' },
  { key: 'signature_request', label: 'Signature Requests', field: 'FilesSignatureRequest' },
  { key: 'vpl_review', label: 'Vehicle Management: Vehicle Photo Log Review', field: 'VehiclePhotoLogReview' },
  { key: 'vplai', label: 'Vehicle Photo Log Hera AI', field: 'VehiclePhotoLogsHeraAi' },
  { key: 'wireless', label: 'Wireless Support Sync', field: 'WirelessSupportSync', requires: { field: 'DeviceManagementV2', label: 'Device Management v2' } },
].sort((a, b) => a.label.localeCompare(b.label))

export const NO_CHANGE = 'none'
export const ON = 'on'
export const OFF = 'off'

export const accessFieldOf = (feature) => `featureAccess${feature.field}`
export const enabledFieldOf = (feature) => `featureEnabled${feature.field}`

export function emptySelections(features = BULK_TOGGLE_FEATURES) {
  return features.reduce((acc, feature) => {
    acc[feature.key] = { access: NO_CHANGE, enabled: NO_CHANGE }
    return acc
  }, {})
}

// Applies the backend's dependency rules so the dialog shows what will really be written:
// Enabled On forces Access On, Access Off forces Enabled Off.
export function resolveSelection(selection) {
  const access = selection?.access || NO_CHANGE
  const enabled = selection?.enabled || NO_CHANGE
  if (access === OFF) {
    return { access: OFF, enabled: OFF, accessForced: false, enabledForced: enabled !== OFF }
  }
  if (enabled === ON) {
    return { access: ON, enabled: ON, accessForced: access !== ON, enabledForced: false }
  }
  return { access, enabled, accessForced: false, enabledForced: false }
}

export function isChanged(selection) {
  const resolved = resolveSelection(selection)
  return resolved.access !== NO_CHANGE || resolved.enabled !== NO_CHANGE
}

// Changes sent to the backend: only the user's own choices (the backend applies the forced ones).
export function buildChanges(selections, features = BULK_TOGGLE_FEATURES) {
  const changes = []
  for (const feature of features) {
    const selection = selections[feature.key]
    if (!selection) continue
    const resolved = resolveSelection(selection)
    if (resolved.access === OFF) {
      changes.push({ field: accessFieldOf(feature), value: false })
      continue
    }
    if (resolved.enabled === ON) {
      changes.push({ field: enabledFieldOf(feature), value: true })
      continue
    }
    if (resolved.access === ON) changes.push({ field: accessFieldOf(feature), value: true })
    if (resolved.enabled === OFF) changes.push({ field: enabledFieldOf(feature), value: false })
  }
  return changes
}

export function countFeatureState(feature, rows = []) {
  const accessField = accessFieldOf(feature)
  const enabledField = enabledFieldOf(feature)
  return rows.reduce((acc, row) => {
    if (row?.[accessField]) acc.accessOn++
    if (row?.[enabledField]) acc.enabledOn++
    return acc
  }, { accessOn: 0, enabledOn: 0, known: rows.length })
}

// One plain-language line per changed feature, for the review step.
// `affected` counts only loaded tenants whose value would actually change; null when nothing is loaded.
export function describeChanges(selections, rows = [], features = BULK_TOGGLE_FEATURES) {
  const lines = []
  for (const feature of features) {
    const selection = selections[feature.key]
    if (!selection || !isChanged(selection)) continue
    const resolved = resolveSelection(selection)
    const accessField = accessFieldOf(feature)
    const enabledField = enabledFieldOf(feature)
    const parts = []
    if (resolved.access !== NO_CHANGE) {
      const target = resolved.access === ON
      parts.push({
        type: 'Access',
        value: target,
        forced: resolved.accessForced,
        affected: rows.length ? rows.filter(row => Boolean(row?.[accessField]) !== target).length : null,
      })
    }
    if (resolved.enabled !== NO_CHANGE) {
      const target = resolved.enabled === ON
      parts.push({
        type: 'Enabled',
        value: target,
        forced: resolved.enabledForced,
        affected: rows.length ? rows.filter(row => Boolean(row?.[enabledField]) !== target).length : null,
      })
    }
    // The required feature is not in the Tenant list rows, so its affected count is unknown
    if (feature.requires && resolved.access === ON) {
      parts.push({
        type: `${feature.requires.label} Access and Enabled`,
        value: true,
        forced: true,
        reason: `needed for ${feature.label}`,
        affected: null,
      })
    }
    lines.push({
      key: feature.key,
      label: feature.label,
      turnsOff: parts.some(part => part.value === false),
      parts,
    })
  }
  return lines
}
